import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import {
  appropriateness,
  type AppropriatenessQuestion,
} from "@/api/appropriateness";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormError,
} from "@/components/ui";

/**
 * MIFID II Art. 25(3) appropriateness questionnaire. Required before
 * a client can unlock leverage above 1:1. Score buckets map to
 * leverage caps (1, 30, 50, 200) — see backend appropriateness.py for
 * the canonical mapping.
 */
export function AppropriatenessPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const state = useQuery({
    queryKey: ["me-appropriateness"],
    queryFn: () => appropriateness.current(),
  });
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      appropriateness.submit(
        Object.entries(answers).map(([qid, idx]) => ({
          question_id: qid,
          option_index: idx,
        })),
      ),
    onSuccess: (s) => {
      toast.push(
        `Submitted — leverage unlock 1:${s.leverage_unlock_max}`,
        s.leverage_unlock_max >= 30 ? "success" : "warn",
      );
      qc.invalidateQueries({ queryKey: ["me-appropriateness"] });
      setAnswers({});
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  const grouped = useMemo(() => {
    const out: Record<string, AppropriatenessQuestion[]> = {};
    for (const q of state.data?.questions ?? []) {
      (out[q.category] ??= []).push(q);
    }
    return out;
  }, [state.data?.questions]);

  if (state.isLoading) {
    return <p className="p-3 text-sm text-term-mute">Loading…</p>;
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Appropriateness questionnaire</h1>

      {state.data && (
        <Card>
          <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Stat
              label="Latest score"
              value={state.data.latest_total_score?.toString() ?? "—"}
            />
            <Stat
              label="Leverage unlock"
              value={`1:${state.data.leverage_unlock_max}`}
              tone={state.data.leverage_unlock_max >= 30 ? "good" : "warn"}
            />
            <Stat
              label="Last completed"
              value={
                state.data.latest_completed_at
                  ? new Date(state.data.latest_completed_at).toLocaleDateString()
                  : "—"
              }
            />
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody>
          <p className="mb-3 text-xs text-term-dim">
            ESMA / CySEC require us to assess your knowledge, experience, and
            financial situation before unlocking leveraged products.{" "}
            <strong>76% of retail accounts lose money trading CFDs with this
            provider.</strong>{" "}
            Please answer truthfully — falsifying answers does not give you
            real protection from losses.
          </p>
        </CardBody>
      </Card>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          submit.mutate();
        }}
        className="space-y-4"
      >
        {(["knowledge", "experience", "financial"] as const).map((cat) => (
          <Card key={cat}>
            <CardHeader>
              <h2 className="text-sm font-semibold capitalize">{cat}</h2>
            </CardHeader>
            <CardBody className="space-y-4">
              {(grouped[cat] ?? []).map((q) => (
                <fieldset key={q.id}>
                  <legend className="mb-2 text-sm font-medium">{q.prompt}</legend>
                  <div className="space-y-1">
                    {q.options.map((opt, idx) => (
                      <label
                        key={idx}
                        className="flex items-center gap-2 text-xs cursor-pointer"
                      >
                        <input
                          type="radio"
                          name={q.id}
                          checked={answers[q.id] === idx}
                          onChange={() =>
                            setAnswers((a) => ({ ...a, [q.id]: idx }))
                          }
                          required
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </CardBody>
          </Card>
        ))}

        <FormError>{err}</FormError>
        <div className="flex justify-end">
          <Button type="submit" loading={submit.isPending}>
            Submit
          </Button>
        </div>
      </form>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "warn";
}) {
  const cls =
    tone === "good"
      ? "text-emerald-500"
      : tone === "warn"
      ? "text-amber-500"
      : "text-slate-900 dark:text-slate-100";
  return (
    <div>
      <dt className="text-xs uppercase text-slate-500">{label}</dt>
      <dd className={`tnum font-medium ${cls}`}>{value}</dd>
    </div>
  );
}
